"use client";
import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { useContactModal } from '@/contexts/ContactModalContext';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';
const LIME = '#ceff1a';
const CARD = '#fbfdfd';

function MarkerUnderline({ delay = 0.6 }: { delay?: number }) {
    return (
        <svg
            className="absolute left-0 -bottom-1 w-full pointer-events-none"
            height="12"
            viewBox="0 0 200 12"
            preserveAspectRatio="none"
            aria-hidden="true"
        >
            <motion.path
                d="M2,7 C34,3 58,10 92,5 C118,1 150,9 198,6"
                stroke={LIME}
                strokeWidth="6"
                strokeLinecap="round"
                fill="none"
                opacity="0.7"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.7, delay, ease: 'easeOut' }}
            />
        </svg>
    );
}

/* A slightly lopsided, hand-drawn circle — not a perfect ellipse */
function RoughCircle() {
    return (
        <svg
            className="absolute -inset-3 pointer-events-none"
            viewBox="0 0 160 60"
            fill="none"
            aria-hidden="true"
        >
            <motion.path
                d="M22,6 C55,-3 118,-1 145,14 C160,23 152,45 122,52 C88,60 30,58 12,44 C-3,32 3,12 22,6 Z"
                stroke={PURPLE}
                strokeWidth="2.5"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 0.85 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 1.05, ease: 'easeInOut' }}
            />
        </svg>
    );
}

function CurlyArrow() {
    return (
        <svg width="46" height="54" viewBox="0 0 46 54" fill="none" aria-hidden="true" className="shrink-0">
            <motion.path
                d="M40,4 C36,22 22,34 6,42"
                stroke={PURPLE}
                strokeWidth="2"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 0.8 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 1.3 }}
            />
            <motion.path
                d="M2,32 L6,42 L16,40"
                stroke={PURPLE}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 0.8 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: 1.85 }}
            />
        </svg>
    );
}

function CheckIcon() {
    return (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
            <path d="M2.5 6.8l2.6 2.6L10.5 3" stroke={PURPLE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

interface Row {
    label: string;
    flagged?: boolean;
}

const rows: Row[] = [
    { label: 'Crawlability & indexation' },
    { label: 'Site architecture' },
    { label: 'Core Web Vitals', flagged: true },
    { label: 'Structured data' },
    { label: 'Redirects & canonicals' },
];

function AuditPanel() {
    return (
        <div className="relative w-full max-w-[380px] mx-auto lg:mx-0">
            {/* washi tape */}
            <div
                className="absolute -top-4 left-10 w-16 h-7 pointer-events-none"
                style={{
                    background: 'rgba(206,255,26,0.55)',
                    transform: 'rotate(-6deg)',
                    boxShadow: '0 2px 6px rgba(8,4,0,0.08)',
                }}
            />

            <motion.div
                initial={{ opacity: 0, y: 24, rotate: 0 }}
                whileInView={{ opacity: 1, y: 0, rotate: -2.5 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.35, ease: 'easeOut' }}
                className="relative rounded-[22px] p-5"
                style={{
                    background: CARD,
                    border: '1.5px solid rgba(8,4,0,0.1)',
                    boxShadow: '0 24px 60px -20px rgba(8,4,0,0.25)',
                }}
            >
                {/* letterhead */}
                <div className="flex items-center justify-between pb-4 mb-4" style={{ borderBottom: '1px dashed rgba(8,4,0,0.14)' }}>
                    <div className="flex items-center gap-2.5">
                        <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold"
                            style={{ background: INK, color: LIME, fontFamily: CLASH }}
                        >
                            LW
                        </div>
                        <div className="flex flex-col leading-none">
                            <span className="text-xs font-bold" style={{ fontFamily: CLASH, color: INK }}>
                                Health Check
                            </span>
                            <span className="text-[11px] font-mono mt-0.5" style={{ color: 'rgba(8,4,0,0.4)' }}>
                                yoursite.com
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <motion.span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ background: PURPLE }}
                            animate={{ opacity: [1, 0.35, 1] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                        />
                        <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: 'rgba(8,4,0,0.4)' }}>
                            live
                        </span>
                    </div>
                </div>

                {/* rows */}
                <div className="flex flex-col gap-1">
                    {rows.map((r, i) => (
                        <div key={r.label} className="relative">
                            <motion.div
                                initial={{ opacity: 0, x: -8 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: 0.55 + i * 0.08 }}
                                className="flex items-center justify-between py-2 px-2.5 rounded-lg"
                                style={{ background: r.flagged ? 'rgba(206,255,26,0.16)' : 'transparent' }}
                            >
                                <div className="flex items-center gap-2.5">
                                    <span
                                        className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                                        style={{ background: 'rgba(55,48,163,0.08)', border: '1px solid rgba(55,48,163,0.18)' }}
                                    >
                                        <CheckIcon />
                                    </span>
                                    <span className="text-[13px] font-medium" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.8)' }}>
                                        {r.label}
                                    </span>
                                </div>
                            </motion.div>
                            {r.flagged && <RoughCircle />}
                        </div>
                    ))}
                </div>

                {/* mobile annotation, no room for the side arrow */}
                <p
                    className="sm:hidden text-center mt-2 rotate-[-1deg] font-semibold text-sm"
                    style={{ fontFamily: MANROPE, color: PURPLE }}
                >
                    ↑ the one everyone skips
                </p>

                {/* annotation pointing at flagged row */}
                <div className="absolute -right-6 top-[122px] hidden sm:flex items-start" style={{ transform: 'translateX(100%)' }}>
                    <div className="rotate-[-4deg]" style={{ marginTop: -6 }}>
                        <CurlyArrow />
                    </div>
                    <span
                        className="whitespace-nowrap -ml-1 mt-1 font-semibold text-sm rotate-[-2deg] inline-block"
                        style={{ fontFamily: MANROPE, color: PURPLE }}
                    >
                        the one everyone skips
                    </span>
                </div>
            </motion.div>

            <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 1.5 }}
                className="text-center mt-5 rotate-[-1.5deg] font-semibold text-sm tracking-[0.02em]"
                style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.55)' }}
            >
                no fluff, just fixes
            </motion.p>
        </div>
    );
}

const tools = [
    { src: '/logos/search-console.svg', alt: 'Google Search Console' },
    { src: '/logos/analytics.svg', alt: 'Google Analytics' },
    { src: '/logos/semrush.svg', alt: 'Semrush' },
];

export default function TechnicalSEOHero() {
    const { openModal } = useContactModal();

    return (
        <section className="relative bg-white pt-0 pb-0 overflow-hidden">
            <div
                className="relative w-full px-6 md:px-16 lg:px-24 pt-36 pb-24 overflow-hidden"
                style={{
                    background: '#ffffff',
                    borderBottomLeftRadius: '80px',
                    borderBottomRightRadius: '80px',
                }}
            >
                {/* dot grid texture, matches the dark sections below */}
                <div
                    className="absolute inset-0 opacity-[0.05] pointer-events-none"
                    style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '34px 34px' }}
                />

                {/* single asymmetric wash, not a mirrored blob pair */}
                <div
                    className="absolute pointer-events-none"
                    style={{
                        top: '-18%',
                        right: '-10%',
                        width: 620,
                        height: 560,
                        background: 'radial-gradient(circle, rgba(55,48,163,0.12) 0%, transparent 68%)',
                        borderRadius: '42% 58% 63% 37% / 41% 44% 56% 59%',
                    }}
                />

                <div className="max-w-[1360px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center relative">

                    {/* Left: copy */}
                    <div className="lg:col-span-7 flex flex-col items-start gap-7 text-left relative">

                        {/* Breadcrumb */}
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="flex items-center gap-2 text-xs md:text-sm font-medium"
                            style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.45)' }}
                        >
                            <Link href="/services/search-visibility" className="hover:underline" style={{ color: 'rgba(8,4,0,0.45)' }}>
                                SEO Services
                            </Link>
                            <span>/</span>
                            <span style={{ color: PURPLE }}>Technical SEO</span>
                        </motion.div>

                        {/* Eyebrow, stamp-style */}
                        <motion.p
                            initial={{ opacity: 0, y: 14, rotate: 0 }}
                            animate={{ opacity: 1, y: 0, rotate: -2 }}
                            transition={{ duration: 0.5, delay: 0.05 }}
                            className="text-xs md:text-sm font-bold tracking-[0.14em] uppercase px-3 py-1 rounded-full"
                            style={{
                                color: PURPLE,
                                fontFamily: CLASH,
                                border: `1.5px solid ${PURPLE}`,
                            }}
                        >
                            Technical SEO
                        </motion.p>

                        {/* H1 */}
                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="text-4xl md:text-5xl lg:text-[3.4rem] font-bold leading-[1.08] tracking-tight"
                            style={{ fontFamily: CLASH, color: INK }}
                        >
                            Technical SEO That Gives Search Engines a{' '}
                            <span className="relative inline-block" style={{ color: PURPLE }}>
                                Clearer Path
                                <MarkerUnderline delay={0.75} />
                            </span>{' '}
                            to Your Website
                        </motion.h1>

                        {/* Description */}
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.22 }}
                            className="flex flex-col gap-4 max-w-xl"
                        >
                            <p
                                className="text-base md:text-lg leading-relaxed"
                                style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.65)', letterSpacing: '0.01em' }}
                            >
                                Your website can have great content and still struggle in search if search engines can&apos;t properly crawl, understand, and index it.
                            </p>
                            <p
                                className="text-base md:text-lg leading-relaxed"
                                style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.65)', letterSpacing: '0.01em' }}
                            >
                                Lead Workz finds and fixes the technical issues that quietly limit visibility — crawlability, indexation, site architecture, Core Web Vitals, redirects, canonicalisation, structured data — so the foundation works harder for search engines and people alike.
                            </p>
                        </motion.div>

                        {/* CTA */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.32 }}
                            className="pt-1"
                        >
                            <button
                                onClick={openModal}
                                className="inline-flex items-center justify-center px-10 py-5 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
                                style={{
                                    background: PURPLE,
                                    color: '#ffffff',
                                    fontFamily: CLASH,
                                    letterSpacing: '0.08em',
                                }}
                            >
                                Get a Technical SEO Audit →
                            </button>
                        </motion.div>

                        {/* Tool trust row */}
                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                            className="flex items-center gap-4 pt-3"
                        >
                            <span
                                className="text-xs font-semibold uppercase tracking-[0.1em]"
                                style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.35)' }}
                            >
                                Audited with
                            </span>
                            <div className="flex items-center gap-3">
                                {tools.map((t) => (
                                    <div
                                        key={t.alt}
                                        className="w-8 h-8 grayscale opacity-50 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
                                    >
                                        <Image src={t.src} alt={t.alt} width={32} height={32} className="w-full h-full object-contain" />
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>

                    {/* Right: audit panel illustration */}
                    <div className="lg:col-span-5">
                        <AuditPanel />
                    </div>

                </div>
            </div>
        </section>
    );
}
